import { NavLink, Outlet, useLocation, Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Home, Leaf, Plus, LayoutGrid, BarChart3, Menu, BookOpen, Flower2, MapPin, Mail, LogOut, User, Users, ShieldCheck, Library, Moon, Sun } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { HeritageSeal } from "@/components/primitives";
import { BrandLogo } from "@/components/BrandLogo";
import { useAuth } from "@/lib/auth";
import { BL_HERO_LOGO } from "@/pages/blackLeaf/assets";
import "@/styles/blackLeaf.css";
import "@/styles/blackLeafDark.css";

const menuItems = [
  { to: "/community", label: "Community", icon: Users, testid: "menu-community" },
  { to: "/database", label: "Database", icon: Library, testid: "menu-database" },
  { to: "/almanack", label: "Almanack", icon: BookOpen, testid: "menu-almanack" },
  { to: "/codex", label: "Codex", icon: BookOpen, testid: "menu-codex" },
  { to: "/catalogue", label: "Catalogue", icon: Flower2, testid: "menu-catalogue" },
  { to: "/black-leaf-catalog", label: "Black Leaf Catalog", icon: Flower2, testid: "menu-bl-catalog" },
  { to: "/about", label: "About", icon: MapPin, testid: "menu-about" },
  { to: "/contact", label: "Contact", icon: Mail, testid: "menu-contact" },
];

const THEME_KEY = "bk_theme";

export default function AppShell() {
  const location = useLocation();
  const nav = useNavigate();
  const { user, signOut } = useAuth();
  const [theme, setTheme] = useState(() => localStorage.getItem(THEME_KEY) || "default");

  useEffect(() => {
    if (theme === "blackleaf-dark") {
      document.body.classList.add("theme-blackleaf-dark");
    } else {
      document.body.classList.remove("theme-blackleaf-dark");
    }
    localStorage.setItem(THEME_KEY, theme);
    return () => document.body.classList.remove("theme-blackleaf-dark");
  }, [theme]);

  const toggleTheme = () => setTheme((t) => t === "blackleaf-dark" ? "default" : "blackleaf-dark");

  const firstName = (user?.name || "").split(" ")[0] || "Gardener";

  return (
    <div className="min-h-screen flex flex-col">
      {/* Top bar */}
      <header
        className="sticky top-0 z-30 backdrop-blur-md bg-cream/80 border-b border-heritage-border"
        data-testid="app-topbar"
      >
        <div className="max-w-3xl mx-auto px-5 py-3 flex items-center justify-between gap-3">
          <Link to="/home" className="flex items-center gap-3 group" data-testid="topbar-home-link">
            {theme === "blackleaf-dark" ? (
              <img src={BL_HERO_LOGO} alt="Black Leaf Dahlia Society" className="bl-seal bl-seal-sm" style={{ width: 44, height: 44 }} />
            ) : (
              <BrandLogo size={36} />
            )}
            <div className="leading-tight">
              <div className="kicker text-[10px]">
                {theme === "blackleaf-dark" ? "Black Leaf Dahlia Society" : "A Gardener's Companion"}
              </div>
              <div className="font-display text-xl text-sage-dark group-hover:text-ink transition-colors">
                {theme === "blackleaf-dark" ? "The Dark Room" : "The Bloom Keeper"}
              </div>
            </div>
          </Link>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={toggleTheme}
              className="chip"
              aria-label={theme === "blackleaf-dark" ? "Light theme" : "Black Leaf dark theme"}
              title={theme === "blackleaf-dark" ? "Back to Bloom Keeper" : "Black Leaf dark room"}
              data-testid="topbar-theme-toggle"
            >
              {theme === "blackleaf-dark" ? <Sun size={14}/> : <Moon size={14}/>}
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger
                className="chip"
                data-testid="topbar-user-menu"
                aria-label="Your account"
              >
                <User size={14} />
                <span className="hidden sm:inline">{firstName}</span>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="bg-cream-soft border-heritage-border min-w-[240px]"
              >
                <DropdownMenuLabel className="kicker">Signed in</DropdownMenuLabel>
                <div className="px-2 pb-2">
                  <div className="font-display text-lg text-sage-dark leading-tight">{user?.name}</div>
                  <div className="text-xs text-ink-soft truncate">{user?.email}</div>
                  {user?.garden_name && <div className="font-display italic text-sm text-ink-soft mt-1">{user.garden_name}</div>}
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild data-testid="topbar-edit-profile">
                  <NavLink to="/profile" className="flex items-center gap-2 cursor-pointer">
                    <User size={14} /> <span className="font-display text-lg">Edit profile</span>
                  </NavLink>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => { nav("/", { replace: true }); setTimeout(() => signOut(), 0); }}
                  data-testid="topbar-signout"
                  className="cursor-pointer text-terracotta-dark focus:text-terracotta-dark"
                >
                  <LogOut size={14} className="mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger
                className="chip"
                data-testid="topbar-menu-button"
                aria-label="Open menu"
              >
                <Menu size={16} />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="bg-cream-soft border-heritage-border min-w-[220px]"
              >
                <DropdownMenuLabel className="kicker">The Gardener's Library</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {menuItems.map((m) => (
                  <DropdownMenuItem key={m.to} asChild data-testid={m.testid}>
                    <NavLink to={m.to} className="flex items-center gap-2 cursor-pointer">
                      <m.icon size={16} className="text-sage" />
                      <span className="font-display text-lg">{m.label}</span>
                    </NavLink>
                  </DropdownMenuItem>
                ))}
                {user?.is_admin ? (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild data-testid="menu-admin-portal">
                      <NavLink to="/admin" className="flex items-center gap-2 cursor-pointer">
                        <ShieldCheck size={16} className="text-terracotta" />
                        <span className="font-display text-lg">Admin portal</span>
                      </NavLink>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild data-testid="menu-admin-community">
                      <NavLink to="/admin/community" className="flex items-center gap-2 cursor-pointer">
                        <ShieldCheck size={16} className="text-terracotta" />
                        <span className="font-display text-lg">The Vault</span>
                      </NavLink>
                    </DropdownMenuItem>
                  </>
                ) : (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild data-testid="menu-admin-login">
                      <NavLink to="/admin/login" className="flex items-center gap-2 cursor-pointer">
                        <ShieldCheck size={16} className="text-sage-dark" />
                        <span className="font-display text-lg">Admin unlock</span>
                      </NavLink>
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Page content */}
      <main className="flex-1 max-w-3xl w-full mx-auto px-5 pb-32" key={location.pathname}>
        <Outlet />
      </main>

      {/* Bottom tab bar */}
      <nav className="fixed bottom-0 inset-x-0 z-30 bottom-safe" data-testid="bottom-tabbar" aria-label="Primary">
        <div className="max-w-3xl mx-auto px-4 pb-3">
          <div className="h-card grid grid-cols-5 items-center px-2 py-2 shadow-seal">
            <Tab to="/home" icon={Home} label="Home" testid="tab-home" location={location} />
            <Tab to="/garden" icon={Leaf} label="Garden" testid="tab-garden" location={location} />
            <NavLink to="/garden/new" data-testid="tab-add" className="flex flex-col items-center justify-center">
              <span className="flex items-center justify-center w-12 h-12 rounded-full bg-sage-dark text-cream shadow-seal hover:bg-sage transition-colors">
                <Plus size={22} />
              </span>
              <span className="text-[11px] mt-1 text-ink-soft">Add</span>
            </NavLink>
            <Tab to="/beds" icon={LayoutGrid} label="Beds" testid="tab-beds" location={location} />
            <Tab to="/stats" icon={BarChart3} label="Stats" testid="tab-stats" location={location} />
          </div>
        </div>
      </nav>
    </div>
  );
}

function Tab({ to, icon: Icon, label, testid, location }) {
  let isActive;
  if (to === "/home") {
    isActive = location.pathname === "/home";
  } else if (to === "/garden") {
    isActive = location.pathname === "/garden" || (location.pathname.startsWith("/garden/") && location.pathname !== "/garden/new");
  } else {
    isActive = location.pathname.startsWith(to);
  }
  return (
    <NavLink to={to} data-testid={testid} className="flex flex-col items-center justify-center py-1 group">
      <Icon size={20} className={`transition-colors ${isActive ? "text-sage-dark" : "text-ink-soft group-hover:text-sage"}`} />
      <span className={`text-[11px] mt-1 transition-colors ${isActive ? "text-sage-dark font-semibold" : "text-ink-soft"}`}>{label}</span>
    </NavLink>
  );
}
